import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register({ name, email, password, referral_code: referralCode || null });
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    // Surface welcome bonus if granted
    const bonus = res.user && window.__lc_last_signup_bonus;
    if (bonus?.amount) {
      toast.success(`🎁 ${bonus.label}: $${bonus.amount.toFixed(2)} added to your wallet!`);
    } else {
      toast.success("Welcome to LoyaltyCircle AI!");
    }
    window.location.href = "/onboarding";
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-emerald-700 text-white p-12 items-center">
        <div>
          <Sparkles className="w-10 h-10 mb-6" />
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/30 text-amber-100 px-3 py-1.5 rounded-full mb-5 text-xs font-semibold uppercase tracking-[0.15em]">
            <span className="text-amber-300">●</span> $10 Welcome Bonus
          </div>
          <h2 className="font-display font-bold text-4xl tracking-tight leading-tight">
            Earn cashback on the things you already buy.
          </h2>
          <p className="text-emerald-50 mt-4 text-lg leading-relaxed">
            Sign up free and we'll add <strong>$10 instantly</strong> to your cashback wallet. Then get an AI-personalized deal feed in 60 seconds.
          </p>
          <ul className="mt-8 space-y-3 text-emerald-50">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> $10 free on signup</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> 50% of every commission paid back to you</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Family Vault & shared cashback</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md border-gray-200 shadow-none">
          <CardContent className="p-8">
            <h1 className="font-display font-bold text-3xl tracking-tight text-gray-900">Create account</h1>
            <p className="text-gray-600 mt-1.5">Free forever. No credit card required.</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="signup-form">
              <div>
                <Label htmlFor="name" className="mb-1.5 block">Full name</Label>
                <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="signup-name" />
              </div>
              <div>
                <Label htmlFor="email" className="mb-1.5 block">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="signup-email" />
              </div>
              <div>
                <Label htmlFor="password" className="mb-1.5 block">Password</Label>
                <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="signup-password" />
              </div>
              <div>
                <Label htmlFor="ref" className="mb-1.5 block">Referral code <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input id="ref" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="RC1A2B3C4D" data-testid="signup-ref" />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="signup-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="signup-submit"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full py-2.5 font-semibold"
              >
                {loading ? "Creating account…" : "Create free account"}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                By signing up you agree to our compliance terms. Cashback is paid only on verified purchases.
              </p>
            </form>

            <p className="text-sm text-gray-600 mt-6 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-emerald-700 font-semibold hover:underline" data-testid="signup-to-login">
                Log in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
